import {
  Alert,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Group,
  Stack,
} from "@assetwatch/ui";
import { useEffect, useState } from "react";
import { getAssetPickerLocationName } from "../lib/assetPickerTree";
import { parseAssigneeValue } from "../lib/assigneeTokens";
import type { AssetId, WorkOrderId } from "../lib/workIds";
import {
  type CreateWorkOrderDraft,
  isForbiddenError,
} from "../mutations/useCreateWorkOrderMutation";
import { useAssetPickerQuery } from "../queries/useAssetPickerQuery";
import { useAssetTreeQuery } from "../queries/useAssetTreeQuery";
import { useProceduresQuery } from "../queries/useProceduresQuery";
import {
  type TeamsUsersResponse,
  useTeamsUsersQuery,
} from "../queries/useTeamsUsersQuery";
import { useWorkOrderLookupsQuery } from "../queries/useWorkOrderLookupsQuery";
import { ApproveWorkOrderForm } from "./ApproveWorkOrderForm";
import { getProcedureOptions } from "./procedureOptions";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { useDraftProcedureSelection } from "./useDraftProcedureSelection";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";

type AssetWorkOrderDraft = CreateWorkOrderDraft;

/**
 * Only Technicians and Administrators carry out Work Orders, so other roles
 * are left out of the assignee picker; the API rejects them too.
 */
const ASSIGNABLE_ROLE_NAMES = new Set(["Technician", "Administrator"]);

function withAssignableUsers(
  teamsUsers: TeamsUsersResponse | undefined,
): TeamsUsersResponse | undefined {
  if (!teamsUsers) return teamsUsers;
  return {
    ...teamsUsers,
    users: teamsUsers.users.filter(
      (user) => user.role !== null && ASSIGNABLE_ROLE_NAMES.has(user.role),
    ),
  };
}

function toAssignmentIds(assignees: string[]) {
  const targets = assignees.map(parseAssigneeValue);
  return {
    assignedUserIds: targets.flatMap((target) =>
      "userId" in target ? [target.userId] : [],
    ),
    assignedTeamIds: targets.flatMap((target) =>
      "teamId" in target ? [target.teamId] : [],
    ),
  };
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: {
    id: number;
    assetId: AssetId | null;
    name: string;
    locationName: string | null;
  } | null;
  onCreate: (draft: AssetWorkOrderDraft) => Promise<WorkOrderId>;
  onAttachProcedure: (
    workOrderId: WorkOrderId,
    procedureId: string,
  ) => Promise<void>;
  onCreated: (workOrderId: WorkOrderId) => void;
};

export function AssetWorkOrderModal({
  open,
  onOpenChange,
  asset,
  onCreate,
  onAttachProcedure,
  onCreated,
}: Props) {
  const assetTreeQuery = useAssetTreeQuery();
  const pickerQuery = useAssetPickerQuery();
  const { data: lookups } = useWorkOrderLookupsQuery();
  const { data: teamsUsers } = useTeamsUsersQuery();
  const procedureQuery = useProceduresQuery("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [workType, setWorkType] = useState("REACTIVE");
  const [assignees, setAssignees] = useState<string[]>([]);
  const [estimatedHours, setEstimatedHours] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const { procedure, setProcedure, resetProcedure, hasProcedureChanges } =
    useDraftProcedureSelection({
      open,
      draftKey: `${procedureQuery.scopeKey}:${asset?.assetId ?? ""}`,
      data: procedureQuery.data,
    });
  const procedureOptions = getProcedureOptions(
    procedureQuery.data?.items,
    procedureQuery.data?.defaultProcedure,
  );
  const [vendor, setVendor] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [createdWorkOrderId, setCreatedWorkOrderId] =
    useState<WorkOrderId | null>(null);
  // Set when attaching was refused for lack of permission: retrying cannot
  // succeed, so the footer offers to open the Work Order instead.
  const [attachDenied, setAttachDenied] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<{
    id: number | null;
    assetId: AssetId | null;
    name: string;
  }>({
    id: asset?.id ?? null,
    assetId: asset?.assetId ?? null,
    name: asset?.name ?? "",
  });
  const guard = useUnsavedChangesGuard({
    hasUnsavedChanges:
      open &&
      (title !== "" ||
        description !== "" ||
        priority !== "MEDIUM" ||
        workType !== "REACTIVE" ||
        assignees.length > 0 ||
        estimatedHours !== "" ||
        startDate !== "" ||
        dueDate !== "" ||
        vendor !== "" ||
        selectedAsset.assetId !== (asset?.assetId ?? null) ||
        hasProcedureChanges),
  });
  function requestClose() {
    guard.requestLeave(() => onOpenChange(false));
  }

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDescription("");
    setPriority("MEDIUM");
    setWorkType("REACTIVE");
    setAssignees([]);
    setEstimatedHours("");
    setStartDate("");
    setDueDate("");
    setVendor("");
    setError(null);
    setCreatedWorkOrderId(null);
    setAttachDenied(false);
    setSelectedAsset({
      id: asset?.id ?? null,
      assetId: asset?.assetId ?? null,
      name: asset?.name ?? "",
    });
  }, [asset?.assetId, asset?.id, asset?.name, open]);

  const onReset = () => {
    setTitle("");
    setDescription("");
    setPriority("MEDIUM");
    setWorkType("REACTIVE");
    setAssignees([]);
    setEstimatedHours("");
    setStartDate("");
    setDueDate("");
    resetProcedure();
    setVendor("");
  };

  async function submit() {
    if (asset && !selectedAsset.assetId) {
      setError("Select a valid asset before creating this Work Order.");
      return;
    }
    if (!title.trim() || !description.trim()) return;
    const hours = estimatedHours.trim() ? Number(estimatedHours) : null;
    if (hours !== null && (!Number.isFinite(hours) || hours < 0)) {
      setError("Estimated hours must be zero or greater.");
      return;
    }
    setPending(true);
    setError(null);
    let workOrderWasCreated = createdWorkOrderId !== null;
    try {
      const workOrderId =
        createdWorkOrderId ??
        (await onCreate({
          title: title.trim(),
          description: description.trim(),
          instructions: "",
          assetId: selectedAsset.assetId,
          workTypeCode: workType,
          priorityCode: priority,
          ...toAssignmentIds(assignees),
          basicStartDate: startDate || null,
          basicFinishDate: dueDate || null,
          estimatedMinutes: hours === null ? null : Math.round(hours * 60),
          vendorName: vendor.trim() || null,
        }));
      if (!createdWorkOrderId && procedure !== "NONE") {
        setCreatedWorkOrderId(workOrderId);
        workOrderWasCreated = true;
      }
      if (procedure !== "NONE") {
        await onAttachProcedure(workOrderId, procedure);
      }
      guard.allowNavigation();
      onCreated(workOrderId);
      onOpenChange(false);
    } catch (cause) {
      const detail = cause instanceof Error ? cause.message : null;
      if (!workOrderWasCreated) {
        setError(detail ?? "Unable to create Work Order.");
      } else if (isForbiddenError(cause)) {
        setAttachDenied(true);
        setError(
          "Work Order created, but you do not have permission to attach this procedure. Open the Work Order to continue.",
        );
      } else {
        setError(
          `Work Order created, but its procedure was not attached. ${detail ?? "Try again to attach the procedure."}`,
        );
      }
    } finally {
      setPending(false);
    }
  }

  function openCreatedWorkOrder() {
    if (!createdWorkOrderId) return;
    guard.allowNavigation();
    onCreated(createdWorkOrderId);
    onOpenChange(false);
  }

  const approval = {
    title,
    description,
    asset: {
      id: selectedAsset.id ?? 0,
      code: null,
      name: selectedAsset.name,
      locationName:
        asset?.locationName ??
        (selectedAsset.assetId
          ? getAssetPickerLocationName(
              pickerQuery.data?.options ?? [],
              selectedAsset.assetId,
              assetTreeQuery.data?.nodes ?? [],
            )
          : null),
    },
  };

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) requestClose();
          else onOpenChange(true);
        }}
      >
        <DrawerContent size="default">
          <DrawerHeader
            title={title || "Create Work Order"}
            subtitle="CREATE WORK ORDER"
            closeLabel="Close Work Order form"
          />
          <DrawerBody>
            <Stack gap={16}>
              <ApproveWorkOrderForm
                approval={approval}
                showVendor={false}
                lookups={lookups}
                teamsUsers={withAssignableUsers(teamsUsers)}
                procedures={procedureOptions}
                onRetryProcedures={() => void procedureQuery.refetch()}
                procedureStatus={
                  procedureQuery.isPending
                    ? "loading"
                    : procedureQuery.isError
                      ? "error"
                      : "ready"
                }
                resetLabel="Reset form"
                readonlyDetails={createdWorkOrderId !== null}
                procedureDisabled={pending}
                selectedAssetTreeId={selectedAsset.id}
                assetRequired={asset !== null}
                selectedAssetId={selectedAsset.assetId}
                onPublicAssetChange={
                  asset
                    ? undefined
                    : (assetId) => {
                        const option = pickerQuery.data?.options.find(
                          (item) => item.id === assetId,
                        );
                        setSelectedAsset({
                          id: null,
                          name: option?.name ?? "",
                          assetId,
                        });
                      }
                }
                title={title}
                description={description}
                priority={priority}
                workType={workType}
                assignees={assignees}
                estimatedHours={estimatedHours}
                startDate={startDate}
                dueDate={dueDate}
                procedure={procedure}
                vendor={vendor}
                onTitleChange={setTitle}
                onDescriptionChange={setDescription}
                onPriorityChange={setPriority}
                onWorkTypeChange={setWorkType}
                onAssigneesChange={setAssignees}
                onEstimatedHoursChange={setEstimatedHours}
                onStartDateChange={setStartDate}
                onDueDateChange={setDueDate}
                onProcedureChange={(value) => {
                  setProcedure(value);
                  setError(null);
                }}
                onVendorChange={setVendor}
                onReset={onReset}
              />
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            {/* The error sits with the footer actions so it stays in view
              next to Retry rather than below the fold of a long form. */}
            <div className="w-full">
              <Stack gap={12}>
                {error ? <Alert tone="negative" title={error} /> : null}
                <Group justify="end">
                  <Button variant="outline" onClick={requestClose}>
                    Cancel
                  </Button>
                  {attachDenied ? (
                    <Button onClick={openCreatedWorkOrder}>
                      Open Work Order
                    </Button>
                  ) : (
                    <Button
                      disabled={
                        (!createdWorkOrderId &&
                          (!procedureQuery.data ||
                            (asset !== null && !selectedAsset.assetId) ||
                            !title.trim() ||
                            !description.trim())) ||
                        pending
                      }
                      loading={pending}
                      onClick={() => void submit()}
                    >
                      {createdWorkOrderId
                        ? "Retry procedure attachment"
                        : "Create work order"}
                    </Button>
                  )}
                </Group>
              </Stack>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <UnsavedChangesDialog {...guard} />
    </>
  );
}
